export interface copilotFlow {
  messageFrom: string;
  recordType: RecordType;
  message?: any;
  interactions?: any;
  links?: copilotAsset[];
  images?: copilotAsset[];
  process?: copilotAsset[];
  sentimentScore?: Sentiment;
  emailTemplate?: any;
  journeySummaryText?: string;
}

export enum RecordType {
  JourneyHistory = 'journeyHistory',
  Sentiment = 'sentiment',
  SuggestionArticle = 'suggestionArticle',
  Compliance = 'compliance',
  SalesOffer = 'salesOffer',
  NBR = 'nbr',
  BehavioralGuidance = 'behavioralGuidance',
  IntentAndAssistance = 'intentAndAssistance',
  CustomerReply = 'customerReply',
  ElevationData = 'elevationData',
}

export interface copilotAsset {
  webLinkLabel?: string;
  webLinkURL?: string;
  imgSrc?: string;
  processTitle?: string;
  processDescription?: string;
}

export enum Sentiment {
  NEGATIVE = 0,
  NEUTRAL = 1,
  POSITIVE = 2,
}

export const customCopilotTemplate = {
  journeyHistory: {
    journeySummaryText:
      'Caroline has previously completed travel booking on the website, with positive sentiment.',
    interactions: [
      {
        CustomerName: 'Caroline Rich',
        InteractionDateTime: '2023-9-20 10:22:00.000',
        InteractionDurationInSeconds: 172,
        InteractionOrigin: 'Customer Service',
        InteractionReason: 'Compare Plans',
        InteractionSentiment: 'Neutral',
        InteractionSummary:
          'The customer logged into the online portal to compare various travel plans',
        InteractionType: 'WebSiteVisit',
        IsResolved: true,
        StepsTakenDuringCall: 'Compare Plans',
      },
      {
        InteractionType: 'WebSiteVisit',
        InteractionReason: 'Book Travel Plan',
        InteractionDateTime: '2023-8-02 8:04:00.000',
        InteractionSentiment: 'Neutral',
        IsResolved: true,
        InteractionOrigin: 'Customer Service',
        StepsTakenDuringCall: 'Book Travel Plan',
        InteractionDurationInSeconds: 170,
        CustomerName: 'Caroline Rich',
        InteractionSummary:
          'The customer logged into the online portal to complete a travel booking to Europe',
      },
    ],
  },
  botTranscriptData: [
    {
      messageFrom: 'bot',
      message: 'Hello! Welcome to OneTravel. How can I assist you today?',
    },
    {
      messageFrom: 'customer',
      message:
        "Hi, I'm looking to book a 10-day trip to Iceland. Can you provide me with some package options?",
    },
    {
      messageFrom: 'bot',
      message:
        'Sure! I can help you with that. Could you please specify your travel dates',
    },
    {
      messageFrom: 'customer',
      message:
        "I haven't decided on the exact dates yet, but I'm thinking sometime in June. Can you show me the available packages for June?",
    },
    {
      messageFrom: 'bot',
      message:
        'Great choice! June is a fantastic time to visit Iceland. Let me check the available packages for you. One moment, please.',
    },
    {
      messageFrom: 'bot',
      message:
        "I'm sorry, but it looks like I need more specific dates to provide accurate package options. Could you provide a tentative start date?",
    },
    {
      messageFrom: 'customer',
      message: "Let's say June 15th. Can you show me the options now?",
    },
    {
      messageFrom: 'bot',
      message:
        'Thank you! I will look up the packages starting from June 15th. Just a moment, please!',
    },
    {
      messageFrom: 'bot',
      message:
        "I'm having trouble retrieving the package options for those dates. Let me transfer you to a human agent who can assist you further.",
    },
    {
      messageFrom: 'bot',
      message: 'Please hold on for a moment while I connect you with an agent.',
    },
  ],
  rtSummary:
    'Caroline would like to plan a trip from SLC to Iceland. She was given a 7-day itinerary based on her needs.',
  finalAutoSummary:
    'Customer wanted help booking her trip to Iceland. It was booked for June 20th departing out of SLC arriving in KEF. Agent created a 7-day itinerary for her trip that covered all her destinations of choice and sent it to her via email. Customer was satisfied with the resolution of the chat.',
  agentTransferSummary:
    'Caroline Called into customer support looking to book a trip to iceland',
  copilotAgentAndCustomerChatData: [
    {
      messageFrom: 'agent',
      recordType: RecordType.NBR,
      message: [
        'Hello, thank you for contacting OneTravel, my name is Daniel. I see you’re trying to book your trip to Iceland and have questions.',
      ],
    },
    {
      messageFrom: 'customer',
      recordType: RecordType.CustomerReply,
      message: [
        "Hi, I've been looking at all the things to do in Iceland for my trip from Salt Lake City, and there are just too many options. I don't even know where to start.",
      ],
    },
    {
      messageFrom: 'copilot',
      recordType: RecordType.Sentiment,
      message: [
        {
          description:
            'Customer is overwhelmed trying to plan their trip to Iceland.',
        },
      ],
      sentimentScore: Sentiment.NEGATIVE,
    },
    {
      messageFrom: 'copilot',
      recordType: RecordType.BehavioralGuidance,
      message: [
        'The customer loves Nature and Adventure. Previous conversation indicates that the customer is likely to visit some Glacier',
      ],
    },

    {
      messageFrom: 'agent',
      recordType: RecordType.NBR,
      message: [
        `I completely understand, planning a trip can be overwhelming. Don't worry, I'm here to help you. Let's take it step by step. Can you tell me a bit more about your interests and how long you plan to stay in Iceland?`,
      ],
    },
    {
      messageFrom: 'customer',
      recordType: RecordType.CustomerReply,
      message: [
        `Well, I'm going for a week, and I love nature and adventure. I definitely want to see the famous natural attractions like the Blue Lagoon, Golden Circle, and maybe some glacier hiking.`,
      ],
    },
    {
      messageFrom: 'copilot',
      recordType: RecordType.SuggestionArticle,
      message: [
        {
          kbAnswers: {
            title: 'Planning your trip to Iceland',
            description: `Suggested 7-Day Iceland Itinerary:\n
Day 1: Arrival in Reykjavik, relax at the Blue Lagoon.\n
Day 2: Take a Golden Circle tour to see Þingvellir National Park, Geysir, and Gullfoss waterfall.\n
Day 3: Explore the South Coast, visiting Seljalandsfoss and Skogafoss waterfalls, and the black sand beaches of Reynisfjara.\n
Day 4: Head to Vatnajökull National Park for glacier hiking and a visit to Jokulsarlon Glacier Lagoon.\n
Day 5: Continue exploring the East Fjords, known for their stunning landscapes and charming fishing villages.\n
Day 6: Return to Reykjavik, visit Hallgrimskirkja church and explore the city.\n
Day 7: Depart from Reykjavik.`,
          },
          kbInternalUse: {
            kbPrivateResponseTitle: 'Internal Use Only',
            title: 'Iceland Visa Status ',
            kbPrivateResponseTooltip: 'For Internal Use Only',
            description:
              'If a client is outside the U.S. let them know that they may require a visa from Iceland and should plan their vacation at least two months out.',
            kbPrivateShareableAssets: true,
            privateFeedback: true,
          },
          kbLinks: [
            {
              text: 'Visiting Vatnajökull National Park',
              link: 'https://www.google.com/',
            },
            {
              text: 'Jokulsarlon Glacier Lagoon',
              link: 'https://www.google.com/',
            },
            {
              text: '7 Days in Iceland',
              link: 'https://www.google.com/',
            },
          ],
          kbImages: [
            {
              name: 'iceland1',
              imageUrl: './assets/iceland1.png',
            },
            {
              name: 'iceland2',
              imageUrl: './assets/iceland2.png',
            },
          ],
        },
      ],
    },

    {
      messageFrom: 'customer',
      recordType: RecordType.CustomerReply,
      message: [
        'That sounds amazing! Can you help me book these activities and accommodations?',
      ],
    },
    {
      messageFrom: 'copilot',
      recordType: RecordType.Sentiment,
      message: [
        {
          description:
            'The customer is feeling better about her trip and feeling less overwhelmed',
        },
      ],
      sentimentScore: Sentiment.NEUTRAL,
    },
    {
      messageFrom: 'agent',
      recordType: RecordType.NBR,
      message: [
        "Absolutely ! I can arrange all your tours and accommodations to make it hassle-free. Let's start with your flight. Do you have specific dates in mind for your trip?",
      ],
    },
    {
      messageFrom: 'customer',
      recordType: RecordType.CustomerReply,
      message: [
        "I was thinking of going in late June. It's a good time to visit, right?",
      ],
    },
    {
      messageFrom: 'copilot',
      recordType: RecordType.SuggestionArticle,
      message: [
        {
          kbAnswers: {
            title: 'Weather in Iceland in June',
            description:
              'June is the first of three summer months in Iceland along with July and August. The summer in Iceland is beautiful, the lush is in full bloom and there is so much to do as we have daylight 24/7. The average temperature in June is 9°C (48°F) but it can go as high as 15°C (59°F) or more.',
          },
          kbInternalUse: {},
          kbLinks: [
            {
              text: 'Visiting Vatnajökull National Park',
              link: 'https://www.google.com/',
            },
            {
              text: 'Jokulsarlon Glacier Lagoon',
              link: 'https://www.google.com/',
            },
            {
              text: '7 Days in Iceland',
              link: 'https://www.google.com/',
            },
          ],
          kbImages: [
            {
              name: 'iceland1',
              imageUrl: './assets/iceland1.png',
            },
          ],
        },
      ],
    },
    {
      messageFrom: 'customer',
      recordType: RecordType.CustomerReply,
      message: ["Sure, let's say I want to depart on June 20th."],
    },
    {
      messageFrom: 'agent',
      recordType: RecordType.NBR,
      message: [
        "Excellent choice ! Let me find some flight options for you. I'll also check availability for the activities and accommodations in the itinerary I suggested. It may take a moment, but I'll make sure everything is in order.",
      ],
    },
    {
      messageFrom: 'customer',
      recordType: RecordType.CustomerReply,
      message: [
        'Thank you so much for your help. This is already much less stressful.',
      ],
    },
    {
      messageFrom: 'copilot',
      recordType: RecordType.BehavioralGuidance,
      message: [
        'Great job! The customer is feeling less stressed and more confident about their trip.',
      ],
    },
    {
      messageFrom: 'copilot',
      recordType: RecordType.SuggestionArticle,
      message: [
        {
          kbAnswers: {
            title: 'Planning your trip to Iceland',
            description:
              'Iceland is the safest country in the world according to the Global Peace Index. This annual report, published by the Institute for Economics & Peace (IEP), ranks nations’ safety levels. Iceland has been the most peaceful country in the world since 2008, when the index was first launched.',
          },
          kbInternalUse: {},
          kbLinks: [
            {
              text: 'Visiting Vatnajökull National Park',
              link: 'https://www.google.com/',
            },
            {
              text: 'Jokulsarlon Glacier Lagoon',
              link: 'https://www.google.com/',
            },
            {
              text: '7 Days in Iceland',
              link: 'https://www.google.com/',
            },
          ],
          kbImages: [
            {
              name: 'iceland1',
              imageUrl: './assets/iceland1.png',
            },
            {
              name: 'iceland2',
              imageUrl: './assets/iceland2.png',
            },
          ],
          kbProcessSteps: [
            {
              title: 'Obtain their timeline',
              description:
                'Ask if they have specific dates in mind for their trip',
            },
            {
              title: 'Check to see if they need a visa',
              description:
                'This only applies to non-US citizens as Iceland is part of the Schengen Zone.',
            },
            {
              title: 'Verify their email on file',
              description: 'Itinerary info goes to their email.',
            },
          ],
        },
      ],
    },
    {
      messageFrom: 'agent',
      recordType: RecordType.NBR,
      message: [
        "You're very welcome! I'm here to make your trip planning as smooth as possible. I'll get to work on this right away, and I'll send you an email with all the details and options shortly. Here are your next steps to book your trip.",
      ],
    },
    {
      messageFrom: 'copilot',
      recordType: RecordType.IntentAndAssistance,
      message: [
        {
          intent: 'Book tickets for a 10 day trip to Iceland',
          title: 'Book tickets for a 10 day trip to Iceland',
          tasks: [
            {
              order: 0,
              text: 'Hey! Here to help you complete your booking to a 10 day trip to Iceland.',
            },
            {
              order: 1,
              text: "Can you confirm a few things for me?'",
            },
            {
              order: 2,
              text: 'We are booking tickets for the you and your family of (4), correct?',
              buttons: [
                {
                  text: 'Yes',
                  action: 'NextStep',
                },
                {
                  text: 'No',
                  action: 'NextStep',
                },
              ],
            },
            {
              order: 3,
              text: 'Thanks!',
            },
            {
              order: 4,
              text: 'Here are available times for flights boarding from Salt Lake city airport %%date%%',
              buttons: [
                {
                  text: '5:00 PM',
                  action: 'NextStep',
                },
                {
                  text: '7:00 PM',
                  action: 'NextStep',
                },
                {
                  text: '8:00 PM',
                  action: 'NextStep',
                },
              ],
            },
            {
              order: 5,
              text: 'Great. We have booked your flights for %%date%% at %%time%%',
            },
            {
              order: 6,
              text: 'You can find your tickets in your One Travel App and same will be sent to your email',
            },
          ],
        },
      ],
    },
    {
      messageFrom: 'customer',
      recordType: RecordType.CustomerReply,
      message: ["I appreciate it, and I can't wait for my trip to Iceland!"],
    },
    {
      messageFrom: 'copilot',
      recordType: RecordType.Sentiment,
      message: [
        {
          description:
            'The customer feels satisfied with the resolution of their issue.',
        },
      ],

      sentimentScore: Sentiment.POSITIVE,
    },
    {
      messageFrom: 'agent',
      recordType: RecordType.NBR,
      message: [
        `It's my pleasure, and I'm sure you'll have an incredible time in Iceland. If you have any more questions or need assistance with anything else, don't hesitate to reach out. Enjoy your adventure!`,
      ],
    },
    {
      messageFrom: 'elevatedEmail',
      recordType: RecordType.ElevationData,
      message: [
        'Booking your Iceland Trip with OneTravel \n1. Open your email and login to OneTravel \n2. Click the “Accept” button at the top right of the screen to accept your trip \n3. Enter your payment info and have a great trip!',
      ],
      emailTemplate: {
        to: 'caroline.rich@synset.com',
        from: 'daniel.jordon@delta.com',
        subject: 'Booking details confirmation',
      },
    },
    {
      messageFrom: 'elevatedSMS',
      recordType: RecordType.ElevationData,
      message: [
        'Booking your Iceland Trip with OneTravel \n1. Open your email and login to OneTravel \n2. Click the “Accept” button at the top right of the screen to accept your trip \n3. Enter your payment info and have a great trip!',
      ],
    },
  ],
};

export const copilotAgentAndCustomerChatData: copilotFlow[] = [
  {
    messageFrom: 'copilot',
    recordType: RecordType.JourneyHistory,
    interactions: [
      {
        CustomerName: 'Caroline Rich',
        InteractionDateTime: '2023-9-20 10:22:00.000',
        InteractionDurationInSeconds: 172,
        InteractionOrigin: 'Customer Service',
        InteractionReason: 'Compare Plans',
        InteractionSentiment: 'Neutral',
        InteractionSummary:
          'The customer logged into the online portal to compare various travel plans',
        InteractionType: 'WebSiteVisit',
        IsResolved: true,
        StepsTakenDuringCall: 'Compare Plans',
      },
      {
        InteractionType: 'WebSiteVisit',
        InteractionReason: 'Book Travel Plan',
        InteractionDateTime: '2023-8-02 8:04:00.000',
        InteractionSentiment: 'Neutral',
        IsResolved: true,
        InteractionOrigin: 'Customer Service',
        StepsTakenDuringCall: 'Book Travel Plan',
        InteractionDurationInSeconds: 170,
        CustomerName: 'Caroline Rich',
        InteractionSummary:
          'The customer logged into the online portal to complete a travel booking to Europe',
      },
    ],
  },
  {
    messageFrom: 'agent',
    recordType: RecordType.NBR,
    message: [
      'Hello, thank you for contacting OneTravel, my name is Daniel. I see you’re trying to book your trip to Iceland and have questions.',
    ],
  },
  {
    messageFrom: 'customer',
    recordType: RecordType.CustomerReply,
    message: [
      "Hi, I've been looking at all the things to do in Iceland for my trip from Salt Lake City, and there are just too many options. I don't even know where to start.",
    ],
  },
  {
    messageFrom: 'copilot',
    recordType: RecordType.Sentiment,
    message: [
      {
        description:
          'Customer is overwhelmed trying to plan their trip to Iceland.',
      },
    ],
    sentimentScore: Sentiment.NEGATIVE,
  },
  {
    messageFrom: 'copilot',
    recordType: RecordType.BehavioralGuidance,
    message: [
      'The customer loves Nature and Adventure. Previous conversation indicates that the customer is likely to visit some Glacier',
    ],
  },

  {
    messageFrom: 'agent',
    recordType: RecordType.NBR,
    message: [
      `I completely understand, planning a trip can be overwhelming. Don't worry, I'm here to help you. Let's take it step by step. Can you tell me a bit more about your interests and how long you plan to stay in Iceland?`,
    ],
  },
  {
    messageFrom: 'customer',
    recordType: RecordType.CustomerReply,
    message: [
      `Well, I'm going for a week, and I love nature and adventure. I definitely want to see the famous natural attractions like the Blue Lagoon, Golden Circle, and maybe some glacier hiking.`,
    ],
  },
  {
    messageFrom: 'copilot',
    recordType: RecordType.SuggestionArticle,
    message: [
      {
        kbAnswers: {
          title: 'Planning your trip to Iceland',
          description:
            'Iceland is the safest country in the world according to the Global Peace Index. This annual report, published by the Institute for Economics & Peace (IEP), ranks nations’ safety levels. Iceland has been the most peaceful country in the world since 2008, when the index was first launched.',
        },
        kbLinks: [
          {
            text: 'Visiting Vatnajökull National Park',
            link: 'https://www.google.com/',
          },
          {
            text: 'Jokulsarlon Glacier Lagoon',
            link: 'https://www.google.com/',
          },
          {
            text: '7 Days in Iceland',
            link: 'https://www.google.com/',
          },
        ],
        kbImages: [
          {
            name: 'iceland1',
            imageUrl: './assets/iceland1.png',
          },
          {
            name: 'iceland2',
            imageUrl: './assets/iceland2.png',
          },
        ],
      },
    ],
  },

  {
    messageFrom: 'customer',
    recordType: RecordType.CustomerReply,
    message: [
      'That sounds amazing! Can you help me book these activities and accommodations?',
    ],
  },
  {
    messageFrom: 'copilot',
    recordType: RecordType.Sentiment,
    message: [
      {
        description:
          'The customer is feeling better about her trip and feeling less overwhelmed',
      },
    ],
    sentimentScore: Sentiment.NEUTRAL,
  },
  {
    messageFrom: 'agent',
    recordType: RecordType.NBR,
    message: [
      "Absolutely ! I can arrange all your tours and accommodations to make it hassle-free. Let's start with your flight. Do you have specific dates in mind for your trip?",
    ],
  },
  {
    messageFrom: 'customer',
    recordType: RecordType.CustomerReply,
    message: [
      "I was thinking of going in late June. It's a good time to visit, right?",
    ],
  },
  {
    messageFrom: 'copilot',
    recordType: RecordType.SuggestionArticle,
    message: [
      {
        kbAnswers: {
          title: 'Weather in Iceland in June',
          description:
            'June is the first of three summer months in Iceland along with July and August. The summer in Iceland is beautiful, the lush is in full bloom and there is so much to do as we have daylight 24/7. The average temperature in June is 9°C (48°F) but it can go as high as 15°C (59°F) or more.',
        },
        kbLinks: [
          {
            text: 'Visiting Vatnajökull National Park',
            link: 'https://www.google.com/',
          },
          {
            text: 'Jokulsarlon Glacier Lagoon',
            link: 'https://www.google.com/',
          },
          {
            text: '7 Days in Iceland',
            link: 'https://www.google.com/',
          },
        ],
        kbImages: [
          {
            name: 'iceland1',
            imageUrl: './assets/iceland1.png',
          },
        ],
      },
    ],
  },
  {
    messageFrom: 'customer',
    recordType: RecordType.CustomerReply,
    message: ["Sure, let's say I want to depart on June 20th."],
  },
  {
    messageFrom: 'agent',
    recordType: RecordType.NBR,
    message: [
      "Excellent choice ! Let me find some flight options for you. I'll also check availability for the activities and accommodations in the itinerary I suggested. It may take a moment, but I'll make sure everything is in order.",
    ],
  },
  {
    messageFrom: 'customer',
    recordType: RecordType.CustomerReply,
    message: [
      'Thank you so much for your help. This is already much less stressful.',
    ],
  },
  {
    messageFrom: 'copilot',
    recordType: RecordType.BehavioralGuidance,
    message: [
      'Great job! The customer is feeling less stressed and more confident about their trip.',
    ],
  },
  {
    messageFrom: 'copilot',
    recordType: RecordType.SuggestionArticle,
    message: [
      {
        kbAnswers: {
          title: 'Planning your trip to Iceland',
          description:
            'Iceland is the safest country in the world according to the Global Peace Index. This annual report, published by the Institute for Economics & Peace (IEP), ranks nations’ safety levels. Iceland has been the most peaceful country in the world since 2008, when the index was first launched.',
        },
        kbLinks: [
          {
            text: 'Visiting Vatnajökull National Park',
            link: 'https://www.google.com/',
          },
          {
            text: 'Jokulsarlon Glacier Lagoon',
            link: 'https://www.google.com/',
          },
          {
            text: '7 Days in Iceland',
            link: 'https://www.google.com/',
          },
        ],
        kbImages: [
          {
            name: 'iceland1',
            imageUrl: './assets/iceland1.png',
          },
          {
            name: 'iceland2',
            imageUrl: './assets/iceland2.png',
          },
        ],
        kbProcessSteps: [
          {
            title: 'Obtain their timeline',
            description:
              'Ask if they have specific dates in mind for their trip',
          },
          {
            title: 'Check to see if they need a visa',
            description:
              'This only applies to non-US citizens as Iceland is part of the Schengen Zone.',
          },
          {
            title: 'Verify their email on file',
            description: 'Itinerary info goes to their email.',
          },
        ],
      },
    ],
  },
  {
    messageFrom: 'agent',
    recordType: RecordType.NBR,
    message: [
      "You're very welcome! I'm here to make your trip planning as smooth as possible. I'll get to work on this right away, and I'll send you an email with all the details and options shortly. Here are your next steps to book your trip.",
    ],
  },
  {
    messageFrom: 'copilot',
    recordType: RecordType.IntentAndAssistance,
    message: [
      {
        intent: 'Book tickets for a 10 day trip to Iceland',
        title: 'Book tickets for a 10 day trip to Iceland',
        tasks: [
          {
            order: 0,
            text: 'Hey! Here to help you complete your booking to a 10 day trip to Iceland.',
          },
          {
            order: 1,
            text: 'Can you confirm a few things for me?',
          },
          {
            order: 2,
            text: 'We are booking tickets for the you and your family of (4), correct?',
            buttons: [
              {
                text: 'Yes',
                action: 'NextStep',
              },
              {
                text: 'No',
                action: 'NextStep',
              },
            ],
          },
          {
            order: 3,
            text: 'Thanks!',
          },
          {
            order: 4,
            text: 'Here are available times for flights boarding from Salt Lake city airport %%date%%',
            buttons: [
              {
                text: '5:00 PM',
                action: 'NextStep',
              },
              {
                text: '7:00 PM',
                action: 'NextStep',
              },
              {
                text: '8:00 PM',
                action: 'NextStep',
              },
            ],
          },
          {
            order: 5,
            text: 'Great. We have booked your flights for %%date%% at %%time%%',
          },
          {
            order: 6,
            text: 'You can find your tickets in your One Travel App and same will be sent to your email',
          },
        ],
      },
    ],
  },
  {
    messageFrom: 'customer',
    recordType: RecordType.CustomerReply,
    message: ["I appreciate it, and I can't wait for my trip to Iceland!"],
  },
  {
    messageFrom: 'copilot',
    recordType: RecordType.Sentiment,
    message: [
      {
        description:
          'The customer feels satisfied with the resolution of their issue.',
      },
    ],

    sentimentScore: Sentiment.POSITIVE,
  },
  {
    messageFrom: 'agent',
    recordType: RecordType.NBR,
    message: [
      `It's my pleasure, and I'm sure you'll have an incredible time in Iceland. If you have any more questions or need assistance with anything else, don't hesitate to reach out. Enjoy your adventure!`,
    ],
  },
  {
    messageFrom: 'elevatedEmail',
    recordType: RecordType.ElevationData,
    message: [
      'Booking your Iceland Trip with OneTravel \n1. Open your email and login to OneTravel \n2. Click the “Accept” button at the top right of the screen to accept your trip \n3. Enter your payment info and have a great trip!',
    ],
    emailTemplate: {
      to: 'caroline.rich@synset.com',
      from: 'daniel.jordon@delta.com',
      subject: 'Booking details confirmation',
    },
  },
  {
    messageFrom: 'elevatedSMS',
    recordType: RecordType.ElevationData,
    message: [
      'Booking your Iceland Trip with OneTravel \n1. Open your email and login to OneTravel \n2. Click the “Accept” button at the top right of the screen to accept your trip \n3. Enter your payment info and have a great trip!',
    ],
  },
];
